import { useState } from 'react';
import { apiFetch } from '@/lib/api';
import { acceptFor, validateUpload } from '@/lib/uploadPolicy';
import { Upload, FileImage, FileVideo, File, Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  storagePath: string;
  url?: string;
  uploadedAt: Date;
}

interface UploadProgress {
  id: string;
  name: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

export default function InputsPage() {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <FileImage className="h-8 w-8" />;
    if (type.startsWith('video/')) return <FileVideo className="h-8 w-8" />;
    return <File className="h-8 w-8" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const uploadFile = async (file: File) => {
    const uploadId = crypto.randomUUID();

    // Add to progress tracking
    setUploadProgress((prev) => [...prev, { id: uploadId, name: file.name, progress: 0, status: 'uploading' }]);

    try {
      // Reject disallowed types up front; the backend enforces the same allowlist.
      const validation = validateUpload(file);
      if (!validation.ok) throw new Error(validation.error);
      const { contentType } = validation;

      // Step 1: Get presigned URL from backend
      const presignedRes = await apiFetch('/uploads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          contentType,
        }),
      });

      if (!presignedRes.ok) {
        const data = (await presignedRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || 'Failed to get upload URL');
      }

      const { uploadUrl, storagePath } = (await presignedRes.json()) as {
        uploadUrl: string;
        storagePath: string;
        expiresAt: string;
      };

      // Step 2: Upload file to GCS using presigned URL
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          // Must match the contentType the URL was signed with.
          'Content-Type': contentType,
        },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error('Failed to upload file to storage');
      }

      // Update progress to success
      setUploadProgress((prev) => prev.map((p) => (p.id === uploadId ? { ...p, progress: 100, status: 'success' } : p)));

      // Add to uploaded files list
      const newFile: UploadedFile = {
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        type: file.type,
        storagePath,
        uploadedAt: new Date(),
      };

      setUploadedFiles((prev) => [newFile, ...prev]);

      // Remove from progress after 2 seconds
      setTimeout(() => {
        setUploadProgress((prev) => prev.filter((p) => p.id !== uploadId));
      }, 2000);
    } catch (err) {
      // Update progress to error
      setUploadProgress((prev) =>
        prev.map((p) =>
          p.id === uploadId
            ? {
                ...p,
                progress: 0,
                status: 'error',
                error: err instanceof Error ? err.message : 'Upload failed',
              }
            : p,
        ),
      );

      // Remove from progress after 5 seconds
      setTimeout(() => {
        setUploadProgress((prev) => prev.filter((p) => p.id !== uploadId));
      }, 5000);
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((file) => {
      uploadFile(file);
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const deleteFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const cancelUpload = (uploadId: string) => {
    setUploadProgress((prev) => prev.filter((p) => p.id !== uploadId));
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Input Assets</h1>
        <p className="text-muted-foreground">
          Upload files to use as inputs for your workflows. Files are stored in the{' '}
          <code className="text-sm bg-muted px-1 py-0.5 rounded">input</code> directory where ComfyUI can access them.
        </p>
      </div>

      {/* Upload Area */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Upload Files</CardTitle>
          <CardDescription>Drag and drop files or click to browse</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              'border-2 border-dashed rounded-lg p-12 text-center transition-colors',
              isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-muted-foreground/50',
            )}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">Drop files here</p>
            <p className="text-sm text-muted-foreground mb-4">Supports images, videos, audio, .txt, .psd and .drx files</p>
            <Button
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.multiple = true;
                input.accept = acceptFor();
                input.onchange = (e) => handleFileSelect((e.target as HTMLInputElement).files);
                input.click();
              }}
            >
              Browse Files
            </Button>
          </div>

          {/* Upload Progress */}
          {uploadProgress.length > 0 && (
            <div className="mt-6 space-y-2">
              <h3 className="text-sm font-medium mb-2">Uploading</h3>
              {uploadProgress.map((upload) => (
                <div key={upload.id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {upload.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />}
                      {upload.status === 'success' && (
                        <div className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      {upload.status === 'error' && (
                        <div className="h-4 w-4 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                          <X className="h-3 w-3 text-white" />
                        </div>
                      )}
                      <span className="text-sm truncate">{upload.name}</span>
                    </div>
                    {upload.status === 'uploading' && (
                      <Button variant="ghost" size="sm" onClick={() => cancelUpload(upload.id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {upload.status === 'uploading' && (
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${upload.progress}%` }}
                      />
                    </div>
                  )}
                  {upload.status === 'error' && <p className="text-sm text-red-500 mt-1">{upload.error}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Uploaded Files */}
      <Card>
        <CardHeader>
          <CardTitle>Uploaded Files</CardTitle>
          <CardDescription>
            {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} ready for use
          </CardDescription>
        </CardHeader>
        <CardContent>
          {uploadedFiles.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No files uploaded yet</p>
              <p className="text-sm">Upload files to use them in your workflows</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="border rounded-lg p-4 hover:bg-accent/50 transition-colors group">
                  <div className="flex items-start justify-between mb-3">
                    <div className="text-muted-foreground">{getFileIcon(file.type)}</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteFile(file.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                    <p className="text-xs text-muted-foreground">{file.uploadedAt.toLocaleString()}</p>
                    <p className="text-xs font-mono text-muted-foreground truncate" title={file.storagePath}>
                      {file.storagePath}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
